import React, { useState, useEffect, useContext, useCallback } from "react";
import clsx from "clsx";
import { useHistory } from "react-router-dom";
import { useSnackbar } from "notistack";
import { Theme, makeStyles } from "@material-ui/core/styles";
import {
  Grid,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TablePagination,
  TableContainer,
} from "@material-ui/core";
import { MainContext } from "../../../context/main";
import {
  getProjectList,
  delDeleteProject,
  putEditProject,
} from "../../../apis/project";
import { IProject } from "../../../interface/project";
import { getUserList } from "../../../apis/user";
import ConfirmDelete from "../../../components/confirm-delete";
import ProjectRowTable from "../../../components/project-table/project-rowtable";
import ProjectHeaderTable from "../../../components/project-table/project-headertable";
import { getDepartmentList } from "../../../apis/department";
import { LangContext } from "../../../context/lang";
import { IParamQuery } from "../../../interface/project";
import { sortKeyDate } from "../../../constant/commons";

const useStyle = makeStyles((theme: Theme) => ({
  root: {
    height: `calc(100% - ${theme.spacing(4)}px)`,
    width: `calc(100% - ${theme.spacing(4)}px)`,
    padding: theme.spacing(2),
  },
  btn: { textTransform: "unset" },
  paper: {
    padding: 0,
  },
  primary: { fontFamily: ["Montserrat-Medium", "Kanit-Medium"].join(",") },
  tableContainer: {
    height: `calc(100vh - ${theme.spacing(29)}px)`,
  },
  flexMiddle: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  flexCenter: { display: "flex", justifyContent: "center" },
}));

const checkPermission = (role: string | undefined) =>
  role !== "customer" ? true : false;

const initialParamQuery: IParamQuery = {
  keywords: "",
  createdDate: undefined,
  status: -1,
  order: "asc",
};

const Projects = () => {
  const classes = useStyle();
  const [users, setusers] = useState<any[]>([]);
  const [stateData, setstateData] = useState<IProject[] | undefined>(undefined);
  const [openDelete, setopenDelete] = useState<string | undefined>(undefined);
  const [process, setprocess] = useState<boolean>(false);
  const [processConfirm, setprocessConfirm] = useState<boolean>(false);
  const [departmentList, setdepartmentList] = useState<any>([]);
  const [page, setPage] = React.useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(10);
  const [paramQuery, setparamQuery] = useState<IParamQuery>(initialParamQuery);

  const { userInfo } = useContext(MainContext);
  const { lang } = useContext(LangContext);
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const initData = useCallback(async () => {
    const rsDepList = await getDepartmentList();
    if (rsDepList.data) {
      setdepartmentList(rsDepList.data);
    }
    if (checkPermission(userInfo?.role)) {
      const userList: any = await getUserList();
      userList?.data && setusers(userList.data);
    }
  }, [userInfo]);

  useEffect(() => {
    const abortController = new AbortController();
    getProjectList(undefined, abortController).then((projectList: any) => {
      if (projectList?.data) {
        setstateData(projectList.data);
      } else {
        setstateData([]);
      }
    });
    initData();
    return () => {
      abortController.abort();
    };
  }, [initData]);

  const _onAccept = async () => {
    setprocess(true);
    const rsDelProj: any = await delDeleteProject(openDelete ? openDelete : "");
    if (rsDelProj?.status === 204) {
      const projectList: any = await getProjectList(undefined);
      if (projectList?.data) {
        setstateData(projectList.data);
      } else {
        setstateData([]);
      }
    } else {
      enqueueSnackbar("Oops, something went wrong", { variant: "error" });
    }
    setprocess(false);
    setopenDelete(undefined);
  };

  const _onChangeSelect = (owner: string, target: string) => {
    let newStateData = stateData?.map((sd: IProject) => {
      if (sd._id === owner) {
        sd = { ...sd, responsible_person: target };
      }
      return sd;
    });
    setstateData(newStateData);
  };

  const _onConfirmAssign = async (projId: string) => {
    const updatedProj = stateData?.find((proj) => proj._id === projId);
    if (updatedProj) {
      setprocessConfirm(true);
      const rsUpdateProj = await putEditProject(updatedProj);
      setprocessConfirm(false);
      rsUpdateProj.status?.code === 200
        ? enqueueSnackbar("Updated assign", { variant: "success" })
        : enqueueSnackbar("Oops, something went wrong", { variant: "error" });
    }
  };

  const _onQueryProjects = async () => {
    const projectList: any = await getProjectList(paramQuery);
    if (projectList?.data) {
      setstateData(projectList.data);
    } else {
      setstateData([]);
    }
    setparamQuery({ ...paramQuery, order: initialParamQuery.order });
  };

  const _clearQueryParam = async () => {
    setparamQuery({ ...initialParamQuery, createdDate: "" });
    const projectList: any = await getProjectList(undefined);
    if (projectList?.data) {
      setstateData(projectList.data);
    } else {
      setstateData([]);
    }
  };

  const _onSortDate = () => {
    setparamQuery({
      ...paramQuery,
      order: paramQuery.order === "asc" ? "desc" : "asc",
    });

    setstateData(
      sortKeyDate(
        stateData,
        "created_at",
        paramQuery.order === "asc" ? "desc" : "asc"
      )
    );
  };

  return (
    <div className={classes.root}>
      <ConfirmDelete
        open={Boolean(openDelete)}
        onClose
        cancel={() => {
          setopenDelete(undefined);
        }}
        accept={_onAccept}
        process={process}
      />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Button
            className={classes.btn}
            variant="contained"
            color="primary"
            onClick={() => {
              history.push("/app/design/project-detail");
            }}
          >
            {lang.data.createProject}
          </Button>
        </Grid>
        {stateData && stateData.length === 0 ? (
          <Grid item xs={12}>
            <Paper elevation={2} style={{ textAlign: "center", padding: 24 }}>
              <Typography variant="h6" className={classes.primary}>
                {lang.data.noProject}
              </Typography>
              <Typography variant="body1">
                {lang.data.pleaseCreateProject}
              </Typography>
            </Paper>
          </Grid>
        ) : stateData && stateData.length > 0 ? (
          <Grid item xs={12}>
            <Paper elevation={2} className={classes.paper}>
              <TableContainer className={classes.tableContainer}>
                <Table stickyHeader aria-label="simple table">
                  <TableHead>
                    <ProjectHeaderTable
                      paramQuery={paramQuery}
                      setparamQuery={setparamQuery}
                      queryProjects={_onQueryProjects}
                      clearParam={_clearQueryParam}
                      onSortDate={_onSortDate}
                    />
                  </TableHead>
                  <TableBody>
                    {stateData
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((row: IProject) => (
                        <ProjectRowTable
                          key={row._id}
                          data={row}
                          users={users}
                          clickDelete={(id: string) => {
                            setopenDelete(id);
                          }}
                          departments={departmentList}
                          process={processConfirm}
                          changeResponsiblePerson={_onChangeSelect}
                          confirmAssign={_onConfirmAssign}
                        />
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                labelRowsPerPage={lang.data.rowPerPage}
                component="div"
                count={stateData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
              />
            </Paper>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <div className={clsx(classes.tableContainer, classes.flexMiddle)}>
              <div className={clsx(classes.flexCenter)}>
                <CircularProgress color="primary" />
              </div>
            </div>
          </Grid>
        )}
      </Grid>
    </div>
  );
};

export default Projects;
